import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertTriangleIcon, CheckIcon, CreditCardIcon, HourglassIcon } from 'lucide-react';
import { BookingSubmissionResult } from '../../types/booking';
import { formatNaira } from '../../utils/format';
interface BookingResultProps {
  result: BookingSubmissionResult;
  onEdit: () => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;
const notReturned = 'Not returned';

/** Displays exactly what the backend returned — no defaults are substituted for missing fields. */
export function BookingResult({
  result,
  onEdit
}: BookingResultProps) {
  const {
    response,
    raw,
    httpStatus
  } = result;
  const amounts = [{
    label: 'Base amount',
    value: response.baseAmount
  }, {
    label: 'Early check-in fee',
    value: response.earlyFee
  }, {
    label: 'Late checkout fee',
    value: response.lateFee
  }, {
    label: 'Refundable caution fee',
    value: response.cautionFee
  }].filter((row): row is {
    label: string;
    value: number;
  } => typeof row.value === 'number');
  if (!response.ok) {
    return <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.25,
      ease: EASE
    }} className="rounded-sm bg-danger-50 p-6 sm:p-8" role="alert" data-id="element-42" data-mp="2xrdcaotoogjd">
        <AlertTriangleIcon size={22} className="text-danger-700" aria-hidden="true" data-id="element-43" data-mp="3bp20a5j1s3sn" />
        <h3 className="mt-4 font-display text-2xl text-danger-700" data-id="element-44" data-mp="3goemco23h6u6">Booking not accepted</h3>
        <p className="mt-2 text-[15px] leading-relaxed text-danger-700" data-id="element-45" data-mp="3kp89vgbmkjmj">
          {response.message ?? `The booking system did not accept this request (HTTP ${httpStatus}).`}
        </p>
        <p className="mt-2 text-[13px] text-danger-700/80" data-id="element-46" data-mp="3uf9yc97w441z">Nothing has been booked or charged.</p>
        <button type="button" onClick={onEdit} className="mt-6 inline-flex rounded-full bg-forest-700 px-6 py-3 text-sm text-ivory-100 transition-colors duration-150 ease-premium hover:bg-forest-600" data-id="element-47" data-mp="38yslzrxqf4b">
          Review my details
        </button>
        <RawResponse raw={raw} data-id="element-48" data-mp="3en7m4kwcascq" />
      </motion.div>;
  }
  return <motion.div initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.25,
    ease: EASE
  }} aria-live="polite" data-id="element-49" data-mp="39rjxq7pom38a">
      <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-gold-400 text-forest-800" data-id="element-50" data-mp="3joetwjyhfrje">
        <CheckIcon size={22} aria-hidden="true" data-id="element-51" data-mp="19w3ayihxzwn" />
      </span>
      <h3 className="mt-5 font-display text-3xl text-forest-700" data-id="element-52" data-mp="1ci8wsltu5c8">Booking request received</h3>
      {response.message && <p className="mt-2 text-[15px] leading-relaxed text-muted" data-id="element-53" data-mp="b8lb1ygoi3z6">{response.message}</p>}

      <dl className="mt-8 grid gap-5 rounded-sm bg-ivory-100 p-5 sm:grid-cols-3" data-id="element-54" data-mp="b68wlva4qbqv">
        <Meta label="Booking reference" value={response.bookingId ?? notReturned} strong data-id="element-55" data-mp="6a9znjaqjleu" />
        <Meta label="Booking status" value={response.bookingStatus ?? notReturned} data-id="element-56" data-mp="2d0hv5wgwwm5k" />
        <Meta label="Payment status" value={response.paymentStatus ?? notReturned} data-id="element-57" data-mp="2826mvq69mjpg" />
      </dl>

      <div className="mt-5 flex items-start gap-3 rounded-sm bg-gold-100 p-4 text-[13px] leading-relaxed text-forest-700" data-id="element-58" data-mp="nk5kqvnu7qhb">
        <HourglassIcon size={16} className="mt-0.5 shrink-0 text-gold-600" aria-hidden="true" data-id="element-59" data-mp="sh4c56g49jbt" />
        <p data-id="element-60" data-mp="dsrfe797fwg9">Your booking remains pending until payment has been verified. Our booking system will email your confirmation details.</p>
      </div>

      {(amounts.length > 0 || typeof response.totalAmount === 'number') && <div className="mt-8" data-id="element-61" data-mp="4uphw1oojlxq">
          <p className="text-[11px] uppercase tracking-overline text-gold-700" data-id="element-62" data-mp="8u6pp1rkwv62">
            Charges from the booking system{response.currency ? ` (${response.currency})` : ''}
          </p>
          <ul className="mt-3 divide-y divide-forest-700/10 border-y border-forest-700/10 text-[14px]" data-id="element-63" data-mp="3vy4errr911s6">
            {amounts.map(row => <li key={row.label} className="flex justify-between gap-4 py-3" data-id="element-64" data-mp="1p8pscm7i72mr">
                <span className="text-muted" data-id="element-65" data-mp="1u685ypwgu613">{row.label}</span>
                <span className="text-ink" data-id="element-66" data-mp="3qa5tmckwrtf0">{formatNaira(row.value)}</span>
              </li>)}
            {typeof response.totalAmount === 'number' && <li className="flex items-baseline justify-between gap-4 py-4" data-id="element-67" data-mp="3lc4eg2qlse8t">
                <span className="text-[11px] uppercase tracking-overline text-gold-700" data-id="element-68" data-mp="3h8es7jxei8qy">Total amount</span>
                <span className="font-display text-2xl text-forest-700" data-id="element-69" data-mp="3ca4qsrl32bww">{formatNaira(response.totalAmount)}</span>
              </li>}
          </ul>
        </div>}

      <div className="mt-8 rounded-sm bg-danger-50 p-5" role="alert" data-id="element-70" data-mp="32js9d88keyuz">
        <div className="flex items-center gap-3" data-id="element-71" data-mp="2xlgyqvag4kbp">
          <CreditCardIcon size={18} className="text-danger-700" aria-hidden="true" data-id="element-72" data-mp="2xm08hopy02en" />
          <p className="text-[14px] text-danger-700" data-id="element-73" data-mp="2tlg90ehwcmpc">Payment link not available</p>
        </div>
        <p className="mt-2 text-[13px] leading-relaxed text-danger-700" data-id="element-74" data-mp="2ojqzx4e8ij3w">
          Your booking was received, but the booking system did not return a Paystack payment link, so you have not been
          charged. Please contact us with your booking reference to complete payment — do not pay through any other link.
        </p>
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-3" data-id="element-75" data-mp="144ulgqovlect">
        <Link to="/" className="inline-flex rounded-full bg-forest-700 px-6 py-3 text-sm text-ivory-100 transition-colors duration-150 ease-premium hover:bg-forest-600" data-id="element-76" data-mp="192ma9bu84kcs">
          Back to home
        </Link>
      </div>

      <RawResponse raw={raw} data-id="element-77" data-mp="gbcd54fjwtb7" />
    </motion.div>;
}
function Meta({
  label,
  value,
  strong = false
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  const missing = value === notReturned;
  return <div data-id="element-78" data-mp="pc0v2a82fdtd">
      <dt className="text-[10px] uppercase tracking-overline text-gold-700" data-id="element-79" data-mp="u4tvwn0ey206">{label}</dt>
      <dd className={`mt-1.5 break-all ${missing ? 'text-[13px] italic text-muted' : strong ? 'font-display text-lg text-forest-700' : 'text-[15px] text-ink'}`} data-id="element-80" data-mp="u6ois6v8w46w">
        {value}
      </dd>
    </div>;
}
function RawResponse({
  raw
}: {
  raw: unknown;
}) {
  return <details className="group mt-6 text-[12px]" data-id="element-81" data-mp="14xlybcuafdoe">
      <summary className="cursor-pointer text-muted transition-colors duration-150 hover:text-forest-700" data-id="element-82" data-mp="100vi8pihgi2z">View full response</summary>
      <pre className="mt-3 max-h-64 overflow-auto rounded-sm bg-forest-800 p-4 text-[11px] leading-relaxed text-ivory-200" data-id="element-83" data-mp="1enyih3y6x4u0">
        {JSON.stringify(raw, null, 2)}
      </pre>
    </details>;
}